import { formatAddressSlug } from '@mollusk/shared';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useDeferredValue, useState } from 'react';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';
import { useMapboxSessionStore } from '../../stores/mapboxSession';
import { pickOnEnter } from '../permits/permitView';

interface Suggestion {
  mapbox_id: string;
  name: string;
  place_formatted: string;
  full_address: string;
}

/** Address autocomplete that opens the address page (used on Home and Search). */
export function AddressSearchBox({ autoFocus = false }: { autoFocus?: boolean }) {
  const navigate = useNavigate();
  const sessionToken = useMapboxSessionStore((s) => s.sessionToken);
  const [input, setInput] = useState('');
  const deferred = useDeferredValue(input.trim());

  const suggestions = useQuery({
    queryKey: ['autocomplete', deferred, sessionToken],
    enabled: deferred.length >= 3,
    queryFn: () => api.autocomplete(deferred, sessionToken),
  });

  const onPick = (s: Suggestion) => {
    const slug = formatAddressSlug(s.full_address);
    navigate({ to: '/address/$slug', params: { slug }, search: { mid: s.mapbox_id } });
  };

  return (
    <Autocomplete
      freeSolo
      options={suggestions.data?.suggestions ?? []}
      getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.name} — ${o.place_formatted}`)}
      inputValue={input}
      onInputChange={(_, v) => setInput(v)}
      onChange={(_, v) => {
        // A string means Enter on typed text: search the top suggestion (#171).
        const pick = typeof v === 'string' ? pickOnEnter(suggestions.data?.suggestions ?? []) : v;
        if (pick) onPick(pick);
      }}
      loading={suggestions.isFetching}
      filterOptions={(o) => o}
      isOptionEqualToValue={(a, b) => a.mapbox_id === b.mapbox_id}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Address"
          autoFocus={autoFocus}
          placeholder="500 W 2nd St, Austin TX"
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
          error={suggestions.error instanceof ApiError}
          helperText={suggestions.error instanceof ApiError ? suggestions.error.message : ' '}
        />
      )}
    />
  );
}
